import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import GeckoFeatures from 'services/gecko-features';
import FacebookReauthenticateForm from '../components/FacebookReauthenticateForm';
import ChannelEditModal from '../components/ChannelEditModal.jsx';
import ChannelTitle from '../components/ChannelTitle';
import CONSTANTS from 'chat-constants';
import { useBots } from '../../bots/hooks';
import {
  GECKO_CONSTS,
  GeckoDialogService,
  GeckoToastService,
  GeckoModalService,
  GeckoCardTable,
  GeckoModal,
} from 'ui';
import GeckoPagination from 'services/gecko-pagination';

const Channels = (props) => {
  const botsSupported = GeckoFeatures.can('chat-bot');

  const [editingChannel, setEditingChannel] = useState();

  const channels = useSelector((state) => state.channels);
  const fields = useSelector((state) => state.fields);
  const params = useSelector((state) => state.location.params);
  let [bots] = useBots();

  const removeTwilioChannel = (channelId) =>
    GeckoRequest.delete(`/channels/twilio/${channelId}`)
      .then(() => {
        new GeckoToastService().success('Channel removed');
        GeckoLocation.goTo('/settings/channels');
      })
      .catch(renderError);

  const defaultRemoveChannel = (channelId) => {
    return GeckoRequest.delete(`/channel/${channelId}`)
      .then(() => {
        new GeckoToastService().success('Channel removed');
        GeckoLocation.goTo('/settings/channels');
      })
      .catch(renderError);
  };

  const removeChannel = (channelId, channel) => {
    return new GeckoDialogService()
      .confirm('Are you sure you want to remove this Channel?')
      .then((confirm) => {
        if (confirm) {
          switch (channel.type) {
            case 'twilio':
              return removeTwilioChannel(channelId, channel);
            default:
              return defaultRemoveChannel(channelId);
          }
        }
      })
      .catch(() => {});
  };

  const handleChannelForm = (settings) => {
    setEditingChannel(settings);
  };

  const defaultSaveChannel = (channel) => {
    const name = channel.name;
    const config = channel.configuration;
    const hasSuggestionsWithNoConfig = () =>
      config.ai_suggestions && !(config.ai_site_id && config.ai_model);

    if (!name || name === '' || name === ' ') {
      return new GeckoToastService().error('Name is a required field.');
    }

    switch (channel.type) {
      case 'facebook':
      case 'instagram':
        if (!config.page_id || !config.auth_token) {
          return new GeckoToastService().error(
            'You must select a page to subscribe to.'
          );
        }
        break;

      case 'live_chat':
        if (!config.color) {
          return new GeckoToastService().error(
            'Please select a colour for your Live Chat.'
          );
        }
        if (hasSuggestionsWithNoConfig()) {
          return new GeckoToastService().error(
            'Please enter a valid Site ID and Model for your Live Chat.'
          );
        }
        if (
          config.has_advanced_settings &&
          (!config.close_icon || !config.open_icon)
        ) {
          return new GeckoToastService().error(
            'Please upload both and open and close icon.'
          );
        }
        break;

      case 'twilio':
        if (!config.country_code) {
          return new GeckoToastService().error('Please select a country.');
        }
        break;

      case 'mailgun':
        if (!config.pretty_email) {
          return new GeckoToastService().error(
            'Please specify an address to receive emails.'
          );
        }
        break;

      case 'whatsapp':
        break;

      case 'twitter':
        // Nothing really to check except name?
        break;

      case 'wechat':
        break;

      default:
        return;
    }

    let newChannel;
    if (channel.configuration.pre_chat_form) {
      const newObject = { ...channel };
      const findPreChatField = newObject.configuration.pre_chat_form.map(
        (preChatField) => {
          const key = preChatField.key ? preChatField.key : preChatField;
          const required =
            preChatField.required === undefined ? true : preChatField.required;

          const newFields = fields.find((field) => field.key === key);
          //Code below implies possibility of non truthies being produced here so only add required if the field exists
          return newFields ? { ...newFields, required } : newFields;
        }
      );
      const newPreChatFields = findPreChatField.filter((field) => field); // Filter out non truthy fields(null, undefined)
      const newConfig = {
        ...newObject.configuration,
        pre_chat_form: newPreChatFields,
      };
      newChannel = { ...channel, configuration: newConfig };
    } else {
      newChannel = channel;
    }
    return GeckoRequest.post(`/channel/${channel.id}`, newChannel)
      .then(() => {
        setEditingChannel(() => {
          return new GeckoModalService().close('Close channel edit modal');
        });
        GeckoLocation.reload();
        return new GeckoToastService().success('Your changes have been saved.');
      })
      .catch(renderError);
  };

  const saveTwilioChannel = (channel) =>
    GeckoRequest.post(`/channels/twilio/${channel.id}`, {
      name: channel.name,
      forwardTo: channel.configuration.forwarding_number,
    })
      .then(() => {
        setEditingChannel(() => {
          return new GeckoModalService().close('Close channel edit modal');
        });
        GeckoLocation.reload();
        return new GeckoToastService().success('Your changes have been saved.');
      })
      .catch(renderError);

  const saveChannel = () => {
    const channel = editingChannel;
    switch (channel.type) {
      case 'twilio':
        return saveTwilioChannel(channel);
      default:
        return defaultSaveChannel(channel);
    }
  };

  const cancelEditing = () => {
    setEditingChannel(null);
  };

  const openEditModal = (channel) => {
    if (channel.configuration.pre_chat_form) {
      const newFields = channel.configuration.pre_chat_form
        .map((field) => {
          if (field.key) {
            const required =
              // if undefined set true
              field.required === undefined ? true : field.required;
            return { key: field.key, label: field.label, required };
          }
          return field;
        })
        .filter((field) => field !== null);
      channel.configuration.pre_chat_form = newFields;
    }
    setEditingChannel(channel);
  };

  const getChannelColumns = () => {
    const nameColumn = {
      key: 'name',
      name: 'Name',
      width: '100%',
      renderComponent: (value, row) => {
        const style = CONSTANTS.CHANNEL.DETAILS[row.type];
        return (
          <ChannelTitle
            icon={style.ICON}
            color={style.BRAND_COLOUR}
            name={value}
          />
        );
      },
    };

    const typeColumn = {
      key: 'type',
      name: 'Type',
      render: (type) => {
        return CONSTANTS.CHANNEL.DETAILS[type].NAME;
      },
    };

    const botColumn = {
      key: 'bots',
      name: 'Active Bots',
      icon: 'far fa-question-circle',
      description: 'Bots are not supported on email channels',
    };

    const buttonColumn = {
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      items: [
        {
          name: 'Edit',
          handler: (row) => openEditModal(row),
        },
        {
          name: 'Re-authenticate',
          handler: (row) => {
            new GeckoModalService()
              .open(() => {
                return (
                  <GeckoModal name="Re-Authenticate">
                    <FacebookReauthenticateForm
                      pageId={row.configuration.page_id}
                    />
                  </GeckoModal>
                );
              })
              .then((result) => {
                if (!result) {
                  return;
                }
                if (result instanceof Error) {
                  return new GeckoToastService().error(result.message);
                }
                const authToken = result;
                return GeckoRequest.post(
                  `/channel/facebook/${row.id}/reauthenticate`,
                  { authToken }
                ).then(() => {
                  new GeckoToastService().success(
                    `${row.name} has been re-authenticated`
                  );
                });
              })
              .catch(renderError);
          },
          hideWhen: (row) => row.type !== 'facebook',
        },
        {
          name: 'Remove',
          handler: (row) => removeChannel(row.id, row),
        },
      ],
    };

    if (botsSupported) {
      return [nameColumn, botColumn, typeColumn, buttonColumn];
    }

    return [nameColumn, typeColumn, buttonColumn];
  };

  const headerButtons = [
    {
      preset: GECKO_CONSTS.BTN_PRESET_ADD,
      handler: () => {
        GeckoLocation.goTo('/settings/channels/new');
      },
    },
  ];

  if (editingChannel) {
    new GeckoModalService()
      .open(() => {
        return (
          <ChannelEditModal
            channel={editingChannel}
            onChange={handleChannelForm}
            onSave={saveChannel}
            customFields={fields}
          />
        );
      })
      .then(() => {
        cancelEditing();
      });
  }

  const addBotsToChannels = (channels) => {
    return channels.map((channel) => {
      const channelBots = bots.filter((bot) =>
        bot.supported_channels.includes(channel.id)
      );

      // Unsupported Channel for Bots
      if (channel.type === 'mailgun') {
        return { ...channel, bots: '-' };
      }

      const botNames = channelBots.reduce((_bots, bot, index) => {
        if (index !== 0) {
          return _bots.concat(`, ${bot.name}`);
        }
        return _bots.concat(`${bot.name}`);
      }, '');

      return { ...channel, bots: botNames };
    });
  };

  const getChannelRows = () => {
    const filteredChannels = channels.filter(
      (channel) =>
        channel.deletedAt === null &&
        CONSTANTS.CHANNEL.PICKABLE_TYPES.includes(channel.type)
    );

    if (botsSupported) {
      return addBotsToChannels(filteredChannels);
    }

    return filteredChannels;
  };

  const onSearch = (term) => {
    GeckoLocation.goTo(
      '/settings/channels',
      Object.assign({}, params, { search: term })
    );
  };

  const channelsAvailable = getChannelRows();

  let [filteredChannels, pagination] = GeckoPagination.paginate({
    page: params.page,
    searchTerm: params.search,
    searchKey: 'name',
  })(channelsAvailable);

  const alertDetails = channelsAvailable?.length
    ? { alert: 'There are no results to show.' }
    : {
        alert: 'You have not created any Channels yet.',
        link: '#/settings/channels/new',
      };

  return (
    <div className="content-margin">
      <GeckoCardTable
        name="Channels"
        cols={getChannelColumns()}
        rows={filteredChannels}
        headerBtns={headerButtons}
        rowHandler={openEditModal}
        onSearch={onSearch}
        pagination={pagination}
        link={alertDetails.link}
        alert={alertDetails.alert}
        headerProps={{ searchPlaceholder: 'Search channels' }}
      />
    </div>
  );
};

export default Channels;

Channels.contextTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
};
